import { api } from '@packages/backend/convex/_generated/api';
import {
  COMMAND_STATUSES,
  capitaliseFirstLetter,
  getUserErrorMessage,
  zCommandStatus,
} from '@packages/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { formatDistanceToNow } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ActionDropdown } from '@/components/ActionDropdown';
import { DropdownMenuItemDestructive } from '@/components/DropdownMenuItemDestructive';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RemoteCommandStatusBadge } from './RemoteCommandStatusBadge';

const LIMIT = 100;

export const Route = createFileRoute('/a/remote-commands/')({
  validateSearch: (search) => ({
    status: zCommandStatus.default('PENDING').parse(search.status),
  }),
  component: RemoteCommandListPage,
});

function RemoteCommandListPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const convex = useConvex();
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);

  const {
    data: rawCommands,
    error,
    isError,
    isPending,
  } = useQuery(api.remoteCommands.listByStatus, {
    status: search.status,
    limit: LIMIT,
  });

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return '-';
    return formatDistanceToNow(timestamp, { addSuffix: true });
  };

  const commands = useMemo(() => {
    if (!rawCommands) return [];
    return [...rawCommands].sort((a, b) => b._creationTime - a._creationTime);
  }, [rawCommands]);

  const handleDeleteAll = async () => {
    try {
      await convex.mutation(api.remoteCommands.deleteAll);
      setShowDeleteAllDialog(false);
    } catch (error) {
      toast.error("Couldn't delete commands", {
        description: getUserErrorMessage(error),
      });
    }
  };

  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading commands" />;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Remote Commands</CardTitle>
          <CardAction>
            <ActionDropdown>
              <DropdownMenuItemDestructive onSelect={() => setShowDeleteAllDialog(true)}>
                <Trash2 className="h-4 w-4" />
                Delete all
              </DropdownMenuItemDestructive>
            </ActionDropdown>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Tabs
            value={search.status}
            onValueChange={(value) => {
              navigate({
                from: Route.fullPath,
                search: { status: zCommandStatus.parse(value) },
                replace: true,
              });
            }}
          >
            <TabsList>
              {COMMAND_STATUSES.map((status) => (
                <TabsTrigger key={status} value={status}>
                  {capitaliseFirstLetter(status.toLowerCase())}
                </TabsTrigger>
              ))}
            </TabsList>
            {isPending ? (
              <LoadingIcon className="mt-4" />
            ) : (
              <TabsContent value={search.status}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Created</TableHead>
                      <TableHead>Command Type</TableHead>
                      <TableHead>Target Terminal</TableHead>
                      <TableHead>Status</TableHead>
                      {search.status === 'FAILED' && <TableHead>Error</TableHead>}
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commands.map((command) => (
                      <TableRow key={command._id}>
                        <TableCell>{formatDate(command._creationTime)}</TableCell>
                        <TableCell>
                          <code className="text-sm">{command.commandType}</code>
                        </TableCell>
                        <TableCell>{command.targetPosTerminalUuid}</TableCell>
                        <TableCell>
                          <RemoteCommandStatusBadge status={command.commandStatus} />
                        </TableCell>
                        {search.status === 'FAILED' && (
                          <TableCell>
                            {command.commandResult &&
                            typeof command.commandResult === 'object' &&
                            'error' in command.commandResult ? (
                              <div
                                className="text-sm text-red-600 truncate max-w-[250px]"
                                title={String(command.commandResult.error)}
                              >
                                {String(command.commandResult.error)}
                              </div>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Link
                            to="/a/remote-commands/$commandId"
                            params={{ commandId: command._id }}
                            search={{ status: search.status }}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            View
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                    {commands.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={search.status === 'FAILED' ? 6 : 5}
                          className="text-center py-4"
                        >
                          No commands found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all remote commands?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All remote commands will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAll}>Delete all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
